import { Component, ChangeDetectionStrategy, AfterViewInit, ElementRef, Renderer2, OnDestroy, HostBinding, ChangeDetectorRef, Injector, Optional, Input, Output, EventEmitter } from '@angular/core';
import { isRootPageSelector } from 'app/app-page-helpers';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { BOTTOM_NAV_HEIGHT_CSS_VAR } from 'components/cs-bottom-nav/cs-bottom-nav';
import { ModalPageMarker } from 'providers/modal-page-marker';
import { DestroyGuard } from 'services/browser-utils';
import { PubSubService, navigationStartingEvent, navigationStackChangedEvent, PubSubRegistration } from 'services/pubsub.service';
import { ScrollHideService, ScrollHideRegistration } from 'services/scroll-hide.service';

import { markActionButtonsOwnerPage } from './action-buttons-owner';

import { LL } from 'i18n/i18n.service';

// distance from the screen bottom to the bottom nav's top edge. 67px is only a
// fallback for the moment before the nav has measured itself
const BOTTOM_NAV_OFFSET = `var(${BOTTOM_NAV_HEIGHT_CSS_VAR}, 67px)`;

@Component({
  selector: 'cs-page-action-buttons',
  templateUrl: 'cs-page-action-buttons.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'page-action-buttons'
  }
})
export class CsPageActionButtons implements AfterViewInit, OnDestroy {
  private destroyGuard = new DestroyGuard();
  protected LL = LL;
  @HostBinding('class.visible') visible = false;
  @HostBinding('style.transform') get transform(): string {
    // when not floating, no transform needed
    if (!this.isFloating) return null;

    // when visible, apply scroll-hide transform
    if (this.visible) {
      // above the bottom nav, a scroll-hidden bar must also travel the nav's height,
      // or it stops where the nav was and leaves a gap below it
      if (this.translateY > 0 && this.isAboveBottomNav) {
        return `translateY(calc(${this.translateY}px + ${BOTTOM_NAV_OFFSET}))`;
      }
      return `translateY(${this.translateY}px)`;
    }

    // when hiding with bottom offset (root page), need extra translation to go fully off-screen
    // translateY(100%) only moves by element height, need to add bottom offset too
    if (this.isAboveBottomNav) {
      return `translateY(calc(100% + ${BOTTOM_NAV_OFFSET}))`;
    }

    // let CSS handle default slide-out (translateY(100%))
    return null;
  }

  // whether the page this component belongs to is a root page (has bottom nav)
  // this is determined once at init and never changes, avoiding animation issues
  // caused by the global has-bottom-nav class changing during page transitions
  private pageHasBottomNav = false;

  // set bottom offset when floating on a root page (above bottom nav)
  // keep this offset even when not visible so slide-out animation works correctly from the nav's top edge
  @HostBinding('style.bottom') get bottomOffset(): string {
    if (this.isAboveBottomNav) {
      return BOTTOM_NAV_OFFSET;
    }
    return null; // let CSS handle default (bottom: 0)
  }

  // the bottom nav already covers the home-indicator area, so the scss drops that
  // extra bottom padding here; otherwise the nav hides it and the bar looks lopsided
  @HostBinding('class.above-bottom-nav') get isAboveBottomNav(): boolean {
    return this.isFloating && this.pageHasBottomNav && hasClientSmallScreen();
  }

  @Input() showCancel = false;
  @Output() cancel = new EventEmitter<Event>();

  isFloating = false;
  isInHeader = false;
  private resizeListener: () => void;

  // scroll detection properties
  private translateY = 0;
  private scrollHideRegistration: ScrollHideRegistration;
  private isModalPage = false;

  // track original page for visibility management when page is pushed/popped
  private originalPageElement: HTMLElement | null = null;
  private navigationStartingSubscription: PubSubRegistration | null = null;
  private navigationChangedSubscription: PubSubRegistration | null = null;


  // cancel button only shows on desktop modal pages when opted-in
  get showCancelButton(): boolean {
    return this.showCancel && this.isModalPage && !hasClientSmallScreen();
  }

  onCancelClick(event: Event) {
    this.cancel.emit(event);
  }

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private scrollHideService: ScrollHideService,
    private pubSub: PubSubService,
    @Optional() private modalMarker: ModalPageMarker
  ) { }

  async ngAfterViewInit() {
    // assert: must not be inside cs-content (its *ngIf="!loading" would destroy/recreate us)
    this.assertNotInsideCsContent();

    // store reference to original page element before any positioning
    this.originalPageElement = this.findContainingPageElement();

    // Record the owning page ON the element, right now, while we are still inside that
    // page. Below we may move ourselves into `.split-pane-main-wrapper`, which every page
    // shares — once there the DOM no longer says which page we came from, and cs-content
    // needs that answer to reserve the right amount of room at the bottom of its scroll.
    // See action-buttons-owner.ts for what went wrong without it (#4661).
    markActionButtonsOwnerPage(this.elementRef.nativeElement, this.originalPageElement);

    // determine if this page is a root page (has bottom nav) - set once and never changes
    // this avoids animation issues caused by global has-bottom-nav class changing during transitions
    this.pageHasBottomNav = this.isRootPage();

    // wait for cs-content padding to be set up before positioning.
    // the wait now returns when this component is destroyed instead of hanging
    // (df-3f0eaff27ac6), so the window resize listener registered below has to be
    // skipped in that case — ngOnDestroy has already run, so nothing would remove it
    if (!await this.destroyGuard.safeWaitForAngularUpdate(this.injector)) return;

    // if ModalPageMarker is injected, we're in a modal page
    this.isModalPage = this.modalMarker != null;

    // notify cs-content BEFORE positioning - the event bubbles up from our current
    // location in the DOM tree. If we move to split-pane-main-wrapper first, the
    // event won't reach pageElement and cs-content won't update its padding.
    this.notifyCsContent();

    // initial positioning (may move element to split-pane-main-wrapper on small screens)
    this.updatePosition();

    // listen for window resize to reposition dynamically
    this.resizeListener = () => this.updatePosition();
    window.addEventListener('resize', this.resizeListener);

    // re-run positioning whenever navigation completes. This is the one owner of the
    // navigation-finished subscription for every mode: in header mode it re-attaches the
    // raw-inserted element if a push/pop rebuilt the header DOM, and on every path
    // updatePosition ends by asking shouldBeVisible(), so a page that became active shows
    // again and one that was covered hides
    this.navigationChangedSubscription = this.pubSub.subscribe(navigationStackChangedEvent, () => {
      if (this.destroyGuard.isDestroyed) return;
      this.updatePosition();
    });
  }

  /**
   * Notify cs-content that action buttons have been added by dispatching a custom event.
   */
  private notifyCsContent() {
    // dispatch a custom event that bubbles up to cs-content
    this.elementRef.nativeElement.dispatchEvent(new CustomEvent('actionButtonsAdded', {
      bubbles: true,
      composed: true
    }));
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
    if (this.scrollHideRegistration) {
      this.scrollHideRegistration.unregister();
    }
    if (this.navigationStartingSubscription) {
      this.navigationStartingSubscription.unsubscribe();
    }
    if (this.navigationChangedSubscription) {
      this.navigationChangedSubscription.unsubscribe();
    }

    // if element was moved to split-pane-main-wrapper, remove it from DOM
    // (Angular won't remove it since it's no longer in its original position)
    const element = this.elementRef.nativeElement;
    if (element.parentElement?.classList.contains('split-pane-main-wrapper')) {
      element.remove();
    }
  }

  private shouldShowFloating(): boolean {
    // show floating on small screens
    if (hasClientSmallScreen()) {
      return true;
    }
    // show floating on modal pages
    if (this.isModalPage) {
      return true;
    }
    return false;
  }

  private isFixedSizeMobileModal(): boolean {
    return this.modalMarker?.config?.fullScreenOnMobile === false;
  }

  private updatePosition() {
    if (this.shouldShowFloating()) {
      this.updateFloatingPosition();
    } else {
      this.updateHeaderPosition();
    }
  }

  /**
   * Determine if buttons should be visible based on page active state.
   * - Desktop modal: always visible (contained within modal)
   * - Small screen, header mode, or parked in the shared wrapper: only if page is active
   */
  private shouldBeVisible(): boolean {
    // modals with buttons in flow - always visible (desktop modals and fixed-size mobile modals).
    // not when the bar is parked in the shared split-pane wrapper: that happens when a phone-sized
    // window detached it and a resize then widened the window. There it floats over whichever
    // page is on top, so it may only show while its own page is the active one
    if (this.isModalPage && !this.isParkedInSharedWrapper()
      && (!hasClientSmallScreen() || this.isFixedSizeMobileModal())) {
      return true;
    }
    // small screen or header mode - check if page is active
    return this.isOriginalPageActive();
  }

  /**
   * Whether the element sits in `.split-pane-main-wrapper`, the container every page shares,
   * where tryDetachToSplitPane moves it on small screens.
   */
  private isParkedInSharedWrapper(): boolean {
    return this.elementRef.nativeElement.parentElement?.classList.contains('split-pane-main-wrapper') ?? false;
  }

  /**
   * Set visibility from shouldBeVisible(), the one rule for when the bar is on screen.
   * The only write that does not go through here is the navigation-starting hide, which
   * hides on purpose while the page is still active.
   */
  private refreshVisibility() {
    this.visible = this.shouldBeVisible();
    this.cd.markForCheck();
  }

  /**
   * Handle floating mode positioning (small screens and modals).
   */
  private updateFloatingPosition() {
    const element = this.elementRef.nativeElement;

    // maybe move to split-pane-main-wrapper (small screens only, not fixed-size modals)
    const needsDetach = hasClientSmallScreen()
      && !this.isFixedSizeMobileModal()
      && this.tryDetachToSplitPane(element);

    // set visibility (unless deferred by transition handling in tryDetachToSplitPane)
    if (!needsDetach) {
      this.refreshVisibility();
    }

    this.applyFloatingStyles(element);
    this.ensureScrollHideRegistered();
    this.cd.markForCheck();
  }

  /**
   * Try to detach element to split-pane-main-wrapper.
   * Returns true if detached (visibility handled separately due to possible transition).
   */
  private tryDetachToSplitPane(element: HTMLElement): boolean {
    const splitPaneMainWrapper = document.querySelector('.split-pane-main-wrapper');
    if (!splitPaneMainWrapper || element.parentElement === splitPaneMainWrapper) {
      return false;
    }

    this.unregisterScrollHide();
    splitPaneMainWrapper.appendChild(element);

    const navElement = document.querySelector('cs-nav');
    this.setupPageVisibilityObserver(navElement);

    // handle visibility based on transition state
    if (navElement?.classList.contains('page-transition-active')) {
      this.waitForTransitionThenShow(navElement);
    } else {
      this.refreshVisibility();
    }

    return true;
  }

  /**
   * Wait for page transition to complete, then show if page is still active.
   */
  private waitForTransitionThenShow(navElement: Element) {
    const observer = new MutationObserver(() => {
      if (!navElement.classList.contains('page-transition-active')) {
        observer.disconnect();
        if (!this.destroyGuard.isDestroyed) {
          this.refreshVisibility();
        }
      }
    });
    observer.observe(navElement, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * Handle header mode positioning (desktop non-modal).
   */
  private updateHeaderPosition() {
    const element = this.elementRef.nativeElement;

    // header mode does not hide when a navigation starts, so drop the floating path's
    // navigation-starting subscription if it was set up before a resize brought us here
    this.unsubscribeFromNavigationStarting();

    // this method also runs on every completed navigation (see ngAfterViewInit). We
    // raw-insert this element into the page's cs-header, which Angular does not own —
    // a push/pop can rebuild the header DOM and orphan it (e.g. opening a sub-page like
    // the recipe scanner and coming back). The re-insert below is idempotent (guarded on
    // parent), so re-running on every return is a no-op once the element is back.

    // if page is not active, hide and exit early
    if (!this.shouldBeVisible()) {
      this.refreshVisibility();
      this.unregisterScrollHide();
      return;
    }

    // find the cs-header on the same page
    const pageElement = this.originalPageElement || this.findContainingPageElement();
    const headerRight = (pageElement || document).querySelector('cs-header .header-right');

    if (headerRight && element.parentElement !== headerRight) {
      headerRight.insertBefore(element, headerRight.firstChild);
      this.applyHeaderStyles(element);
    }

    this.refreshVisibility();
    this.unregisterScrollHide();
  }

  private applyFloatingStyles(element: HTMLElement) {
    this.renderer.removeClass(element, 'page-action-buttons--header');
    this.renderer.addClass(element, 'page-action-buttons--floating');
    this.isFloating = true;
    this.isInHeader = false;
    this.updateButtonSizeAttributes(element, false);
  }

  private applyHeaderStyles(element: HTMLElement) {
    this.renderer.removeClass(element, 'page-action-buttons--floating');
    this.renderer.addClass(element, 'page-action-buttons--header');
    this.isFloating = false;
    this.isInHeader = true;
    this.updateButtonSizeAttributes(element, true);
  }

  private ensureScrollHideRegistered() {
    if (this.scrollHideRegistration) return;

    this.scrollHideRegistration = this.scrollHideService.register({
      element: this.elementRef.nativeElement,
      direction: 'down',
      onScrollChange: (translateY) => {
        this.translateY = translateY;
        this.cd.markForCheck();
      },
      enabled: () => this.isFloating && !(this.isModalPage && !hasClientSmallScreen()),
      // above the bottom nav the bar hides whenever the nav hides ('auto'), so it
      // never floats over the empty space the nav left; elsewhere it follows the
      // footer setting like other content ('default')
      hideType: this.isAboveBottomNav ? 'auto' : 'default'
    });

    requestAnimationFrame(() => this.scrollHideService.refresh());
  }

  private unregisterScrollHide() {
    if (!this.scrollHideRegistration) return;
    this.scrollHideRegistration.unregister();
    this.scrollHideRegistration = null;
    this.translateY = 0;
  }

  private unsubscribeFromNavigationStarting() {
    this.navigationStartingSubscription?.unsubscribe();
    this.navigationStartingSubscription = null;
  }

  /**
   * Update size attributes on all cs-button elements based on whether we're in header or floating.
   * In header: use small size. Floating/normal: use medium size.
   */
  private updateButtonSizeAttributes(container: HTMLElement, inHeader: boolean) {
    const buttons = container.querySelectorAll('cs-button');
    buttons.forEach(button => {
      if (inHeader) {
        this.renderer.setAttribute(button, 'small', '');
        this.renderer.removeAttribute(button, 'medium');
      } else {
        this.renderer.setAttribute(button, 'medium', '');
        this.renderer.removeAttribute(button, 'small');
      }
    });
  }

  /**
   * Assert that this component is not inside cs-content.
   * cs-content uses *ngIf="!loading" which destroys/recreates content when loading changes,
   * which would break the button positioning (buttons moved to header would be orphaned).
   */
  private assertNotInsideCsContent() {
    let element = this.elementRef.nativeElement.parentElement;
    while (element) {
      if (element.tagName?.toLowerCase() === 'cs-content') {
        console.error(
          'cs-page-action-buttons must not be placed inside cs-content! ' +
          'Place it as a sibling after cs-content instead. ' +
          'cs-content\'s *ngIf="!loading" would destroy/recreate the buttons when loading changes.'
        );
        break;
      }
      element = element.parentElement;
    }
  }

  /**
   * Find the page element that contains this component by walking up the DOM tree.
   * Page elements have tag names like 'page-log-food', 'page-edit-ingredient', etc.
   */
  private findContainingPageElement(): HTMLElement | null {
    let element = this.elementRef.nativeElement.parentElement;
    while (element) {
      const tagName = element.tagName?.toLowerCase();
      if (tagName?.startsWith('page-')) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  /**
   * Check if our original page is the active page.
   * cs-nav adds 'active' class to the active page.
   */
  private isOriginalPageActive(): boolean {
    return this.originalPageElement?.classList.contains('active') ?? false;
  }

  /**
   * Check if this component's page is a root page (has bottom nav on small screens).
   * Root pages are: HomePage, TrainingPage, NutritionPage, SettingsPage
   */
  private isRootPage(): boolean {
    if (!this.originalPageElement) return false;
    const tagName = this.originalPageElement.tagName?.toLowerCase();
    return isRootPageSelector(tagName);
  }

  /**
   * Floating-detached-only: hide the bar the moment any navigation starts, so it slides
   * out with the page instead of hanging over the next one. Showing it again is not done
   * here: the navigation-finished subscription in ngAfterViewInit re-runs updatePosition,
   * which shows the bar if this page is active again and keeps it hidden if not.
   */
  private setupPageVisibilityObserver(_navElement: Element | null) {
    if (!this.originalPageElement) {
      return;
    }

    // clean up any existing subscription
    this.unsubscribeFromNavigationStarting();

    // subscribe to navigation starting to immediately hide when navigating away
    this.navigationStartingSubscription = this.pubSub.subscribe(navigationStartingEvent, () => {
      if (this.destroyGuard.isDestroyed) return;
      // hide immediately when any navigation starts (CSS transition handles animation).
      // this deliberately does NOT ask shouldBeVisible(): at navigation start the page
      // is usually still the active one, so it would answer true and keep the bar up
      this.visible = false;
      this.cd.markForCheck();
    });
  }
}
